<template>
    <div style="width: 100%">
        <b-table-simple small bordered>
            <thead>
            <tr>
                <th>Status</th>
                <th>Sub Status</th>
                <th>
                    Contact Delay
                    <HelpPopup markdown="Number of hours between auto outbound calls" />
                </th>
                <th>
                    Max Age
                    <HelpPopup markdown="Number of Days after the fill date to stop calling" />
                </th>
                <th>
                    Twilio Flow
                    <HelpPopup markdown="SID of the Twilio Flow" />
                </th>
                <th></th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(config, idx) in statusConfigurations" :key="idx">
                <td>
                    <EnumSelector v-model="config.status" :enum-codes="RefillStatusCode" />
                </td>
                <td class="substatus-cell">
                    <div v-if="applicableSubStatuses(config.status).filter(ss => ss.active).length==0"
                        class="text-center">
                        N/A
                    </div>
                    <template v-else>
                        <v-select
                            :value="valueForConfig(config)"
                            :options="applicableSubStatuses(config.status).filter(ss => ss.active)"
                            @input="config.subStatus = $event?.value"
                            :clearable="false" />
                    </template>
                </td>
                <td>
                    <b-input type="number" v-model.number="config.contactDelay" min="0" />
                </td>
                <td>
                    <b-input type="number" v-model.number="config.fillDateMaxAge" min="0" />
                </td>
                <td>
                    <b-input v-model="config.twilioFlow" />
                </td>
                <td>
                    <b-button variant="link" title="Remove" @click.prevent="removeConfiguration(config)">
                        <b-icon icon="x-circle-fill" variant="danger" />
                    </b-button>
                </td>
            </tr>
            </tbody>
            <tfoot>
            <b-button variant="success" size="sm" class="mx-2"
                @click="addNewConfiguration">
                Add
            </b-button>
            </tfoot>
        </b-table-simple>
    </div>
</template>

<script lang="ts">
import { Component, VModel, Vue } from 'vue-property-decorator';
import { AutoOutboundStatusConfiguration } from "@/models/Configuration";
import EnumSelector from "@/components/EnumSelector.vue";
import { RefillStatusCode } from "@/models/Prescription";
import HelpPopup from "@/components/HelpPopup.vue";

@Component({
    name: "AutoOutboundStatusField",
    components: {
        HelpPopup,
        EnumSelector,
    },
})
export default class AutoOutboundStatusField extends Vue {
    @VModel() statusConfigurations!: AutoOutboundStatusConfiguration[];

    private onHoldSubStatuses: Array<{ value: number, label: string, active: boolean }> = [];
    private discontinuedSubStatuses: Array<{ value: number, label: string, active: boolean }> = [];

    get RefillStatusCode() {
        return RefillStatusCode;
    }

    created() {
        this.getSubStatusConfiguration();
    }

    addNewConfiguration() {
        this.statusConfigurations.push(new AutoOutboundStatusConfiguration());
    }

    removeConfiguration(config: AutoOutboundStatusConfiguration) {
        const idx = this.statusConfigurations.indexOf(config);
        if (idx >= 0) {
            this.statusConfigurations.splice(idx, 1);
        }
    }

    valueForConfig(config: AutoOutboundStatusConfiguration) {
        return {
            value: config.subStatus,
            label: this.applicableSubStatuses(config.status).find(ss => ss.value == config.subStatus)?.label,
        };
    }

    applicableSubStatuses(statusCode: RefillStatusCode) {
        return statusCode == RefillStatusCode.OnHold ? this.onHoldSubStatuses :
            statusCode == RefillStatusCode.Discontinued ? this.discontinuedSubStatuses
                : [];
    }

    async getSubStatusConfiguration() {
        try {
            const resp = await this.$http.get("/api/Configuration");
            this.onHoldSubStatuses = resp.data.onHoldSubStatuses.map((ss: any) => ({
                value: ss.id,
                label: ss.name,
                active: ss.active,
            }));
            this.discontinuedSubStatuses = resp.data.discontinuedSubStatuses.map((ss: any) => ({
                value: ss.id,
                label: ss.name,
                active: ss.active,
            }));
        } catch {
            //ignore
        }
    }

}

</script>

<style scoped lang="scss">
::v-deep(thead th),
::v-deep(td) {
    padding: 1px 7px !important;
}

::v-deep(td) {
    height: 38px !important;
}

::v-deep(.substatus-cell) {
    min-width: 250px;
}

table ::v-deep(.actions) {
    width: 50px;
}

table ::v-deep(.active) {
    width: 40px;
}
</style>
